import { useEffect, useMemo, useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import {
  LayoutDashboard,
  Banknote,
  Workflow,
  Megaphone,
  Users,
  RefreshCw,
  Search,
  ScrollText,
  CalendarDays,
  Briefcase,
  Settings as SettingsIcon,
  Menu,
  X,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useSSE, type SSEStatus } from "@/useSSE";
import { useDashboard } from "@/hooks/useDashboard";
import { useClientBranding } from "@/hooks/useClientBranding";
import { formatAgo } from "@/lib/dashboard";
import { fetchAllClientBrandings } from "@/api";
import ThemeToggle from "@/components/ThemeToggle";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import RoleSwitcher from "@/components/RoleSwitcher";
import NotificationBell from "@/components/NotificationBell";

// Nav i18n keys keyed off path; labels resolved at render time so language
// changes flip immediately without remount.
const NAV_PRIMARY: { to: string; key: string; icon: typeof LayoutDashboard; end?: boolean }[] = [
  { to: "/", key: "nav.overview", icon: LayoutDashboard, end: true },
  { to: "/weekly-plan", key: "nav.weekly_plan", icon: CalendarDays },
  { to: "/wip", key: "nav.wip", icon: Briefcase },
  { to: "/finance", key: "nav.finance", icon: Banknote },
  { to: "/operations", key: "nav.operations", icon: Workflow },
  { to: "/marketing", key: "nav.marketing", icon: Megaphone },
  // Clients renders as an expandable accordion (not a flat NavLink) — see ClientsAccordion below
  { to: "/audit", key: "nav.audit", icon: ScrollText },
];

const NAV_FOOTER: { to: string; key: string; icon: typeof LayoutDashboard }[] = [
  { to: "/settings", key: "nav.settings", icon: SettingsIcon },
];

const SIDEBAR_WIDTH_PX = 224; // tailwind w-56

export default function Layout() {
  const { t } = useTranslation();
  const sse = useSSE();
  const { dataUpdatedAt, refetch, isFetching, selectedTenant, setSelectedTenant, data } =
    useDashboard();
  const { branding, isCobranded } = useClientBranding();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  // When cobranded, header gets a 2px accent underline + client display name
  // takes precedence over Growtify Cockpit title.
  const accentColor = isCobranded ? branding?.accent_color_primary : undefined;

  // Close mobile nav on route change
  useEffect(() => {
    if (!mobileNavOpen) return;
    const handler = () => setMobileNavOpen(false);
    window.addEventListener("popstate", handler);
    return () => window.removeEventListener("popstate", handler);
  }, [mobileNavOpen]);

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 flex">
      {/* Mobile nav overlay backdrop */}
      {mobileNavOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-slate-950/70 backdrop-blur-sm z-30"
          onClick={() => setMobileNavOpen(false)}
        />
      )}

      {/* Side navigation — fixed on desktop, slide-in drawer on mobile */}
      <aside
        className={`fixed left-0 top-0 bottom-0 z-40 flex flex-col border-r border-slate-800 bg-slate-900/70 backdrop-blur transition-transform duration-200 ease-out
          ${mobileNavOpen ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0
        `}
        style={{
          width: `${SIDEBAR_WIDTH_PX}px`,
          ...(isCobranded && accentColor
            ? { borderRightColor: accentColor, borderRightWidth: "2px" }
            : {}),
        }}
      >
        {/* Brand block at top of sidebar */}
        <div className="px-4 py-4 border-b border-slate-800 flex items-center gap-2">
          {isCobranded && branding?.logo_url ? (
            <img
              src={branding.logo_url}
              alt={branding.display_name}
              className="h-7 w-7 object-contain rounded"
            />
          ) : (
            <span
              className="h-7 w-7 rounded-md flex items-center justify-center text-xs font-bold"
              style={{ backgroundColor: accentColor || "#2563EB", color: "white" }}
            >
              G
            </span>
          )}
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold text-slate-100 truncate">
              {isCobranded ? branding?.display_name : t("app.title")}
            </div>
            <div
              className="text-[9px] uppercase tracking-wider text-slate-500 font-medium"
              style={accentColor ? { color: accentColor } : undefined}
            >
              {isCobranded
                ? `${branding?.vertical || "client"} · cobrand`
                : t("app.tagline")}
            </div>
          </div>
          {/* Mobile close */}
          <button
            type="button"
            onClick={() => setMobileNavOpen(false)}
            className="lg:hidden text-slate-400 hover:text-slate-100"
            aria-label="Close navigation"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Primary nav items */}
        <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-0.5">
          {NAV_PRIMARY.slice(0, 6).map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              onClick={() => setMobileNavOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-2.5 px-2.5 py-2 text-sm rounded-md transition-colors
                ${
                  isActive
                    ? "bg-accent/15 text-accent font-medium"
                    : "text-slate-400 hover:text-slate-200 hover:bg-slate-800/60"
                }`
              }
            >
              <item.icon className="h-4 w-4 flex-shrink-0" />
              <span className="truncate">{t(item.key)}</span>
            </NavLink>
          ))}
          {/* Clients — expandable accordion */}
          <ClientsAccordion onChildNav={() => setMobileNavOpen(false)} />
          {/* Audit (last in primary) */}
          {NAV_PRIMARY.slice(6).map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              onClick={() => setMobileNavOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-2.5 px-2.5 py-2 text-sm rounded-md transition-colors
                ${
                  isActive
                    ? "bg-accent/15 text-accent font-medium"
                    : "text-slate-400 hover:text-slate-200 hover:bg-slate-800/60"
                }`
              }
            >
              <item.icon className="h-4 w-4 flex-shrink-0" />
              <span className="truncate">{t(item.key)}</span>
            </NavLink>
          ))}
        </nav>

        {/* Footer nav (Settings etc.) */}
        <div className="border-t border-slate-800 py-2 px-2 space-y-0.5">
          {NAV_FOOTER.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              onClick={() => setMobileNavOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-2.5 px-2.5 py-2 text-sm rounded-md transition-colors
                ${
                  isActive
                    ? "bg-accent/15 text-accent font-medium"
                    : "text-slate-400 hover:text-slate-200 hover:bg-slate-800/60"
                }`
              }
            >
              <item.icon className="h-4 w-4 flex-shrink-0" />
              <span className="truncate">{t(item.key)}</span>
            </NavLink>
          ))}
        </div>
      </aside>

      {/* Main column — offset right of sidebar on desktop */}
      <div
        className="flex-1 flex flex-col min-w-0"
        style={{ paddingLeft: `0px` }}
      >
        <div className="lg:pl-[224px] flex flex-col min-h-screen">
          <header
            className="border-b border-slate-800 bg-slate-900/50 backdrop-blur sticky top-0 z-20"
            style={
              isCobranded && accentColor
                ? { borderBottomColor: accentColor, borderBottomWidth: "2px" }
                : undefined
            }
          >
            <div className="px-4 sm:px-6 py-3 flex items-center gap-3 flex-wrap">
              {/* Mobile hamburger */}
              <button
                type="button"
                onClick={() => setMobileNavOpen(true)}
                className="lg:hidden text-slate-400 hover:text-slate-100"
                aria-label="Open navigation"
              >
                <Menu className="h-5 w-5" />
              </button>

              {/* Tenant filter (when set) */}
              <div className="flex items-baseline gap-3 flex-wrap flex-1 min-w-0">
                {selectedTenant && data && (
                  <FilterChip
                    tenant={selectedTenant}
                    clientName={data.clients.find((c) => c.slug === selectedTenant)?.display_name}
                    onClear={() => setSelectedTenant(null)}
                  />
                )}
              </div>

              {/* Right-aligned controls */}
              <div className="flex items-center gap-2 sm:gap-3 text-[11px] sm:text-xs text-slate-400 ml-auto">
                <SSEIndicator status={sse.status} />
                <span className={isFetching ? "animate-pulse text-accent" : ""}>
                  {isFetching ? t("app.syncing") : dataUpdatedAt ? formatAgo(dataUpdatedAt, t) : ""}
                </span>
                <CommandHint />
                <NotificationBell />
                <RoleSwitcher />
                <ThemeToggle />
                <LanguageSwitcher />
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => refetch()}
                  className="h-7 text-[11px] sm:h-8 sm:text-xs"
                >
                  <RefreshCw className="h-3 w-3 mr-1" />
                  <span className="hidden sm:inline">{t("header.refresh")}</span>
                </Button>
              </div>
            </div>
          </header>

          <main className="flex-1 px-4 sm:px-6 py-6 sm:py-8 space-y-6 sm:space-y-8">
            <Outlet />
          </main>

          <footer className="px-4 sm:px-6 py-6 text-xs text-slate-500 border-t border-slate-800 mt-12">
            {isCobranded && branding ? (
              <a
                href={branding.powered_by_link_target}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-slate-300 transition-colors"
              >
                {branding.powered_by_text} →
              </a>
            ) : (
              t("app.footer")
            )}
          </footer>
        </div>
      </div>
    </div>
  );
}

function SSEIndicator({ status }: { status: SSEStatus }) {
  const { t } = useTranslation();
  const config: Record<SSEStatus, { color: string; pulse: boolean }> = {
    connecting: { color: "bg-amber-400", pulse: true },
    connected: { color: "bg-emerald-400", pulse: false },
    reconnecting: { color: "bg-amber-500", pulse: true },
    disconnected: { color: "bg-slate-600", pulse: false },
    error: { color: "bg-red-500", pulse: false },
  };
  const c = config[status];
  const label = t(`sse.${status}`);
  return (
    <span className="inline-flex items-center gap-1.5" title={`SSE: ${label}`}>
      <span className={`w-2 h-2 rounded-full ${c.color} ${c.pulse ? "animate-pulse" : ""}`} />
      <span className="hidden md:inline">{label}</span>
    </span>
  );
}

/**
 * Header button that opens the command palette (⌘K). Triggers a synthetic
 * keydown so a single source of truth (CommandPalette listener) decides open.
 */
function CommandHint() {
  const { t } = useTranslation();
  const isMac =
    typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform);
  const trigger = () => {
    document.dispatchEvent(
      new KeyboardEvent("keydown", {
        key: "k",
        metaKey: isMac,
        ctrlKey: !isMac,
        bubbles: true,
      })
    );
  };
  return (
    <button
      type="button"
      onClick={trigger}
      title={t("palette.title")}
      className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md border border-slate-700 bg-slate-900 text-[11px] text-slate-400 hover:text-slate-200 hover:border-slate-600"
    >
      <Search className="h-3 w-3" />
      <span className="hidden md:inline">{t("header.search_placeholder")}</span>
      <kbd className="hidden md:inline px-1 rounded bg-slate-800 text-[10px] font-mono">
        {isMac ? t("header.search_kbd_mac") : t("header.search_kbd_other")}
      </kbd>
    </button>
  );
}

function FilterChip({
  tenant,
  clientName,
  onClear,
}: {
  tenant: string;
  clientName?: string;
  onClear: () => void;
}) {
  const { t } = useTranslation();
  return (
    <button
      onClick={onClear}
      className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] rounded-full bg-accent/15 text-accent border border-accent/30 hover:bg-accent/25"
      title={t("header.filter_clear")}
    >
      <span>{t("header.filter_label")}</span>
      <span className="font-medium">{clientName || tenant}</span>
      <span className="opacity-60">✕</span>
    </button>
  );
}

/**
 * ClientsAccordion — expandable Clients sidebar entry.
 *
 * - Collapsed (default): Single "Clients" row with chevron + count badge
 * - Expanded: 12 client rows below, each linking to /clients/:slug
 * - Auto-expand if current route is /clients/:slug (so user sees their context)
 * - Tıklayınca parent: also navigates to /clients (list page)
 * - Each child: NavLink to /clients/:slug (active state)
 *
 * Accent color per child: comes from each client's `accent_color_primary`,
 * shown as a 6px dot before the name.
 */
function ClientsAccordion({ onChildNav }: { onChildNav: () => void }) {
  const { t } = useTranslation();
  const location = useLocation();
  const isClientsRoute = location.pathname.startsWith("/clients");

  // localStorage-backed manual toggle, but default open if on /clients/* route
  const [manualOpen, setManualOpen] = useState<boolean | null>(() => {
    try {
      const v = window.localStorage.getItem("growtify-cockpit-clients-nav-open");
      return v === null ? null : v === "1";
    } catch {
      return null;
    }
  });
  const isOpen = manualOpen ?? isClientsRoute;

  const setOpen = (v: boolean) => {
    setManualOpen(v);
    try {
      window.localStorage.setItem("growtify-cockpit-clients-nav-open", v ? "1" : "0");
    } catch {
      // ignore
    }
  };

  const brandingsQ = useQuery({
    queryKey: ["admin", "client-brandings"],
    queryFn: fetchAllClientBrandings,
    staleTime: 60_000,
  });

  const clients = useMemo(() => {
    const rows = brandingsQ.data?.brandings || [];
    // Tier order: active(1) → active_pre-launch(2) → onboarding(3) → unknown(9)
    // Within tier: external clients first (no "_" prefix), then internal alphabetically
    const tierRank = (s: string | null): number => {
      if (s === "active") return 1;
      if (s === "active_pre-launch") return 2;
      if (s === "onboarding") return 3;
      return 9;
    };
    return [...rows].sort((a, b) => {
      const ta = tierRank(a.client_status);
      const tb = tierRank(b.client_status);
      if (ta !== tb) return ta - tb;
      const aInt = a.tenant_id.startsWith("_");
      const bInt = b.tenant_id.startsWith("_");
      if (aInt !== bInt) return aInt ? 1 : -1;
      return a.display_name.localeCompare(b.display_name);
    });
  }, [brandingsQ.data]);

  return (
    <div className="space-y-0.5">
      {/* Parent row: Clients */}
      <div className="flex items-stretch">
        <button
          type="button"
          onClick={() => setOpen(!isOpen)}
          className="flex items-center justify-center px-1 text-slate-500 hover:text-slate-200"
          aria-label={isOpen ? "Collapse clients" : "Expand clients"}
        >
          {isOpen ? (
            <ChevronDown className="h-3.5 w-3.5" />
          ) : (
            <ChevronRight className="h-3.5 w-3.5" />
          )}
        </button>
        <NavLink
          to="/clients"
          end
          onClick={() => onChildNav()}
          className={({ isActive }) =>
            `flex-1 flex items-center gap-2.5 px-1.5 py-2 text-sm rounded-md transition-colors
            ${
              isActive
                ? "bg-accent/15 text-accent font-medium"
                : "text-slate-400 hover:text-slate-200 hover:bg-slate-800/60"
            }`
          }
        >
          <Users className="h-4 w-4 flex-shrink-0" />
          <span className="truncate">{t("nav.clients", "Clients")}</span>
          {clients.length > 0 && (
            <span className="ml-auto text-[10px] tabular-nums px-1.5 py-0.5 rounded bg-slate-800/60 text-slate-500">
              {clients.length}
            </span>
          )}
        </NavLink>
      </div>

      {/* Children: client list */}
      {isOpen && (
        <ul className="pl-4 space-y-0.5">
          {brandingsQ.isLoading && (
            <li className="px-2.5 py-1 text-[11px] text-slate-600 italic">Loading…</li>
          )}
          {brandingsQ.isError && (
            <li className="px-2.5 py-1 text-[11px] text-red-400 italic">Failed to load</li>
          )}
          {clients.map((c) => {
            // Status badge — only show for non-active (active is the default; no badge for noise reduction)
            const status = c.client_status;
            const isPreLaunch = status === "active_pre-launch";
            const isOnboarding = status === "onboarding";
            const showBadge = isPreLaunch || isOnboarding;
            const badgeText = isPreLaunch ? "PL" : "ONB";
            const badgeColor = isPreLaunch
              ? "bg-amber-500/20 text-amber-300"
              : "bg-yellow-500/20 text-yellow-300";
            const badgeTitle = isPreLaunch
              ? "Pre-launch — soon active"
              : "Onboarding — Day N / 14";
            return (
              <li key={c.tenant_id}>
                <NavLink
                  to={`/clients/${c.tenant_id}`}
                  onClick={() => onChildNav()}
                  className={({ isActive }) =>
                    `flex items-center gap-2 px-2.5 py-1.5 text-[12.5px] rounded-md transition-colors
                    ${
                      isActive
                        ? "bg-accent/10 text-accent"
                        : "text-slate-500 hover:text-slate-200 hover:bg-slate-800/60"
                    }`
                  }
                  title={`${c.tenant_id}${status ? ` · ${status}` : ""}`}
                >
                  <span
                    className="h-2 w-2 rounded-full flex-shrink-0"
                    style={{ backgroundColor: c.accent_color_primary || "#64748B" }}
                  />
                  <span className="truncate flex-1 min-w-0">{c.display_name}</span>
                  {showBadge && (
                    <span
                      className={`flex-shrink-0 text-[9px] tabular-nums px-1 py-px rounded font-medium leading-none ${badgeColor}`}
                      title={badgeTitle}
                    >
                      {badgeText}
                    </span>
                  )}
                </NavLink>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
